import * as React from "react";
import {translate} from "react-i18next";
import _ from "lodash";

// redux
import {connect} from "react-redux";

// router
import {RouteComponentProps} from "react-router-dom";

// services
import {
    Link,
    UserDto,
    RoleDto,
    ResourceListOfUserDto,
    ResourceListOfRoleDto, MenuDto, AssignedUserRoleDto
} from "../../../services/Client";

// components
import Button from "../../../components/Button";
import TextField from "../../../components/TextField";

import {getDtoArray} from "../AdminUtils";
import UsersTable from "./UsersTable";
import UserCreationDialog from "./UserCreationDialog";
import {renderErrorToast} from "../../../utils/FeedbackMessageUtils";
import GeneralActions from "../../../redux/actions/GeneralActions";
import AdminViewActions from "../../../redux/actions/AdminViewActions";
import {ITranslatedComponent} from "../../../redux/reducers/generalReducer";
import {selectClient, selectAdminArealinks, selectRoles, selectUsers} from "../../../redux/selectors";
import {compose, Dispatch} from "redux";
import HATEOASClient from "../../../services/HATEOASClient";
import {IReduxState} from "../../../redux/IReduxState";
import {Checkbox, Dropdown, Icon} from "semantic-ui-react";

interface IMapStateToProps {
    adminAreaLinks?: MenuDto;
    client: HATEOASClient;
    roles?: ResourceListOfRoleDto;
    users?: ResourceListOfUserDto;
}

interface IMapDispatchToProps {
    addTranslationLocation: () => void;
    receiveUsers: (users: ResourceListOfUserDto) => void;
    receiveRoles: (roles: ResourceListOfRoleDto) => void;
}

interface IOwnProps {

}

interface IUsersViewProps extends IOwnProps, IMapStateToProps, IMapDispatchToProps, ITranslatedComponent, RouteComponentProps<any> {
}

interface IUsersViewState {
    /** Whether or not we are waiting for a response from the back end. */
    loadingUsers: boolean;

    /** Whether or not we are waiting for a response from the back end. */
    loadingRoles: boolean;

    /** User name filter. */
    filter: string | undefined;

    /** Users that match the current filter. */
    filteredUsers: UserDto[];

    /** Whether or not to show the user creation dialog. */
    showUserCreationDialog: boolean;

    /** All roles that are assigned to groups */
    filterValues: Map<number, RoleDto>;

    /** Filter based on group view roles */
    roleFilter: string[];

    roleFilterAllSelected: boolean;
}

class UsersView extends React.Component<IUsersViewProps, IUsersViewState> {
    constructor(props: IUsersViewProps) {
        super(props);

        this.state = {
            loadingUsers: false,
            loadingRoles: false,
            filter: undefined,
            filteredUsers: getDtoArray<UserDto>(this.props.users),
            showUserCreationDialog: false,
            filterValues: this.renderFilterValues(),
            roleFilter: [],
            roleFilterAllSelected: false
        };
    }

    componentDidMount() {
        this.props.addTranslationLocation();

        if (!this.props.users) {
            // we need the ResourceListOfUserDto to determine if the current user has
            // the rights to create new users and pass the users into user creation dialog
            // and the users table.
            this.requestUsers();
        }

        if (!this.props.roles) {
            // we need a list of all available roles for the user creation dialog
            // and roles selection in the users table.
            this.requestRoles();
        }
    }

    componentDidUpdate(prevProps: IUsersViewProps, prevState: IUsersViewState) {
        if ((this.props.users !== prevProps.users)) {
            this.setState({filterValues: this.renderFilterValues()});
        }
    }

    requestUsers = (): void => {
        if (!this.props.adminAreaLinks ||
            !this.props.client.GET_userList(this.props.adminAreaLinks).canExecute()) {
            return;
        }

        this.setState({loadingUsers: true});
        this.props.client.GET_userList(this.props.adminAreaLinks).execute()
            .then((value: ResourceListOfUserDto) => {
                this.props.receiveUsers(value);

                let filteredUsers = value.elements || [];
                if (this.state.filter !== undefined) {
                    // select only the users that match the filter
                    filteredUsers = this.applyFilter(filteredUsers, this.state.filter);
                }

                // request was successfully fulfilled; reset loading state
                this.setState({loadingUsers: false, filteredUsers: filteredUsers});

                this.renderFilterValues();
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:GET_USERS",
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingUsers: false
                });
            });
    }

    requestRoles = (): void => {
        if (!this.props.adminAreaLinks ||
            !this.props.client.GET_roleList(this.props.adminAreaLinks).canExecute()) {
            return;
        }

        this.setState({loadingRoles: true});
        this.props.client.GET_roleList(this.props.adminAreaLinks).execute()
            .then((value: ResourceListOfRoleDto) => {
                this.props.receiveRoles(value);

                // request was successfully fulfilled; reset loading state
                this.setState({loadingRoles: false});
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:GET_ROLES",
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingRoles: false
                });
            });
    }

    addUser = (user: UserDto): void => {
        this.setState({loadingUsers: true});

        if (this.props.users) {
            this.props.client.POST_user(this.props.users).execute(user)
                .then((value: Link) => {
                    // TODO: refresh only the one user that we created...
                    // But that my not be enough, changing a single user may have impact
                    // on others, like deputy

                    // user was created succesfully; refresh the redux store.
                    // requestUsers() takes care of resetting the loadingUsers state.
                    this.requestUsers();
                })
                .catch((reason: any) => {
                    window.scrollTo(0, 0);
                    renderErrorToast({
                        message: "ERRORS:CREATE_USER",
                        translationOptions: {userCn: user.cn},
                        details: reason,
                        timestamp: Date.now()
                    });
                    this.setState({
                        loadingUsers: false
                    });
                });
        }
    }

    updateUser = (oldUser: UserDto, newUser: UserDto): void => {
        this.setState({loadingUsers: true});

        this.props.client.PATCH_user(oldUser).execute(newUser)
            .then((value: Link) => {
                // user was updated succesfully; refresh the redux store.
                // requestUsers() takes care of resetting the loadingUsers state.
                this.requestUsers();
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:UPDATE_USER",
                    translationOptions: {userCn: oldUser.cn},
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingUsers: false
                });
            });
    }

    deleteUser = (user: UserDto): void => {
        this.setState({loadingUsers: true});

        this.props.client.DELETE_user(user).execute(user)
            .then((value: Link) => {
                // user was deleted succesfully; refresh the redux store.
                // requestUsers() takes care of resetting the loadingUsers state.
                this.requestUsers();
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:DELETE_USER",
                    translationOptions: {userCn: user.cn},
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingUsers: false
                });
            });
    }

    applyFilter = (dtos: UserDto[], filter: string): UserDto[] => {
        const filterLowerCase = filter.toLowerCase();
        return dtos.filter(dto => _.includes(dto.cn.toLowerCase(), filterLowerCase)
            || dto.name && _.includes(dto.name.toLowerCase(), filterLowerCase)
            || dto.department && _.includes(dto.department.toLowerCase(), filterLowerCase));
    }

    applyRoleFilter = (dtos: UserDto[], groupNameFilter: string | undefined, roleFilter: string[]): UserDto[] => {
        let filteredDtos = dtos;

        if (groupNameFilter) {
            const filterLowerCase = groupNameFilter.toLowerCase();
            filteredDtos = filteredDtos.filter(dto => dto.cn.toLowerCase().includes(filterLowerCase));
        }

        if (roleFilter.length > 0) {
            filteredDtos = filteredDtos.filter((dto) => {
                let test: boolean = false;
                if(dto.assignedRoles && dto.assignedRoles.length > 0) {
                    test = dto.assignedRoles.some((assignedRole) => assignedRole.role
                        ? roleFilter.some(filter => assignedRole.role ? assignedRole.role.name === filter : false)
                        : false
                    );
                }
                return test;
            });
        }

        return filteredDtos;
    }

    renderFilters = (): JSX.Element => (
        <TextField
            placeholder={this.props.t("FILTER_BY_NAME")}
            value={this.state.filter}
            onChange={(event: React.FormEvent<{}>, newValue: string) => {
                const filterValue = newValue !== "" ? newValue : undefined;
                let filteredDtos = getDtoArray<UserDto>(this.props.users);
                if (filterValue) {
                    filteredDtos = this.applyFilter(filteredDtos, filterValue);
                }
                this.setState({filter: filterValue, filteredUsers: filteredDtos});
            }}
        />
    )

    renderTable = (loading: boolean, filteredUsers: UserDto[], roles: RoleDto[]): JSX.Element => (
        <UsersTable
            users={getDtoArray<UserDto>(this.props.users)}
            filteredUsers={filteredUsers}
            roles={roles}
            loading={loading}
            optionalControls={{
                canGetUserDetails: dto => this.props.client.GET_userDetails(dto).canExecute(),
                canUpdateUser: dto => this.props.client.PATCH_user(dto).canExecute(),
                canDeleteUser: dto => this.props.client.DELETE_user(dto).canExecute(),
            }}
            onUpdateUser={(oldDto: UserDto, newDto: UserDto) => (
                this.updateUser(oldDto, newDto)
            )}
            onDeleteUser={(dto: UserDto) => (
                this.deleteUser(dto)
            )}
        />
    )

    renderRefreshBtn = (loading: boolean): JSX.Element => (
        <Button
            label={this.props.t("RELOAD")}
            labelPosition="after"
            icon={"refresh"}
            disabled={loading}
            onClick={() => {
                this.requestUsers();
                this.requestRoles();
            }}
        />
    )

    renderAddUserBtn = (loading: boolean): JSX.Element => (
        <Button
            label={this.props.t("ADD_USER")}
            labelPosition="after"
            icon={"add"}
            disabled={loading}
            onClick={() => {
                this.setState({showUserCreationDialog: true});
            }}
        />
    )

    renderAddUserDlg = (users: UserDto[], roles: RoleDto[]): JSX.Element => (
        <UserCreationDialog
            users={users}
            roles={roles}
            onClose={(newUser?: UserDto) => {
                this.setState({showUserCreationDialog: false});
                if (newUser) {
                    this.addUser(newUser);
                }
            }}
        />
    )

    renderRolesFilter = (loading: boolean): JSX.Element => (
        <Dropdown
            id={'access-tab-role-filter'}
            icon={<Icon name={'filter'} style={{float: 'left'}} />}
            text={this.props.t("ROLES")}
            scrolling={true}
            disabled={loading}
        >
            <Dropdown.Menu>
                <Dropdown.Item
                    onClick={(e) => {
                        let {roleFilter} = this.state;
                        const filterValues = Array.from(this.state.filterValues.values());

                        e.stopPropagation();

                        this.state.roleFilterAllSelected
                            ? roleFilter = []
                            : filterValues.forEach(filter => roleFilter.push(filter.name));

                        let filteredDtos = this.applyRoleFilter(getDtoArray<UserDto>(this.props.users), this.state.filter, roleFilter);

                        this.setState({filteredUsers: filteredDtos, roleFilter: roleFilter, roleFilterAllSelected: !this.state.roleFilterAllSelected});
                    }}
                >
                    <Checkbox
                        checked={this.state.roleFilterAllSelected}
                        label={this.props.t("GLOBAL:SELECT_ALL")}
                        className={'disableClickEvents'}
                    />
                </Dropdown.Item>
                <Dropdown.Header
                    icon='tags'
                    content={this.props.t("GLOBAL:FILTER_ROLES")}
                />
                {this.state.filterValues && Array.from(this.state.filterValues.values()).sort((a, b) => a.name.localeCompare(b.name)).map((key: RoleDto) => {
                    return (
                        <Dropdown.Item
                            key={key.objectId}
                            onClick={(e) => {
                                let {roleFilter} = this.state;

                                e.stopPropagation();

                                !roleFilter.includes(key.name)
                                    ? roleFilter.push(key.name)
                                    : roleFilter = roleFilter.filter(filter => filter !== key.name);

                                const roleFilterAllSelected = roleFilter.length === this.state.filterValues.size ? true : false;

                                let filteredDtos = this.applyRoleFilter(getDtoArray<UserDto>(this.props.users), this.state.filter, roleFilter);

                                this.setState({filteredUsers: filteredDtos, roleFilter: roleFilter, roleFilterAllSelected: roleFilterAllSelected});
                            }}
                        >
                            <Checkbox
                                checked={this.state.roleFilter.includes(key.name)}
                                label={key.name}
                                className={'disableClickEvents'}
                            />
                        </Dropdown.Item>
                    );
                })}
            </Dropdown.Menu>
        </Dropdown>
    )

    renderFilterValues(): Map<number, RoleDto> {
        let filterValues: Map<number, RoleDto> = new Map<number, RoleDto>();
        getDtoArray<UserDto>(this.props.users).forEach((user: UserDto) => {
            if (user.assignedRoles) {
                user.assignedRoles.forEach((assignedRole: AssignedUserRoleDto) => {
                    if (assignedRole.role) {
                        if (!filterValues.has(assignedRole.role.objectId)) {
                            filterValues.set(assignedRole.role.objectId, assignedRole.role);
                        }
                    }
                });
            }
        });
        return filterValues;
    }

    render() {
        const users = getDtoArray<UserDto>(this.props.users);
        const roles = getDtoArray<RoleDto>(this.props.roles);
        const filteredUsers = this.state.filteredUsers;

        return (
            <>
                {
                    this.props.users &&
                    this.props.client.POST_user(this.props.users).canExecute() &&
                    this.renderAddUserBtn(this.state.loadingUsers)
                }
                {
                    this.props.users &&
                    this.renderRolesFilter(this.state.loadingUsers)
                }
                {
                    this.renderRefreshBtn(this.state.loadingUsers)
                }
                <br />
                {
                    this.renderFilters()
                }
                {
                    this.renderTable(this.state.loadingUsers || this.state.loadingRoles, filteredUsers, roles)
                }
                {
                    this.state.showUserCreationDialog &&
                    this.renderAddUserDlg(users, roles)
                }
            </>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    adminAreaLinks: selectAdminArealinks(state),
    client: selectClient(state),
    roles: selectRoles(state),
    users: selectUsers(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>): IMapDispatchToProps => ({
    addTranslationLocation: () => dispatch(GeneralActions.AddTranslationLocation({pattern: "admin/users", substitutes: "Users Management"})),
    receiveUsers: (users: ResourceListOfUserDto) => dispatch(AdminViewActions.ReceiveUsers(users)),
    receiveRoles: (roles: ResourceListOfRoleDto) => dispatch(AdminViewActions.ReceiveRoles(roles)),
});

export default compose<React.ComponentType<IOwnProps>>(
    translate("ADMIN_VIEW"),
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(UsersView);



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/UsersView.tsx